import type {
  AgentReconciliationCreateTicketParams,
  AgentReconciliationCreateTicketResult,
  AgentReconciliationTicketApiItem,
  AgentReconciliationTicketApiResponse,
  AgentReconciliationTicketDetail,
  AgentReconciliationTicketDetailApi,
  AgentReconciliationTicketDetailParams,
  AgentReconciliationTicketItem,
  AgentReconciliationTicketListParams,
  AgentReconciliationTicketListResult,
  AgentTokenApiItem,
  AgentTokenItem,
  AgentTokenListParams,
  AgentTokenListResult,
  AgentTokenMember,
  AgentTokenSaveParams,
  NetRechargeAgencyLine,
  NetRechargeCompareApiItem,
  NetRechargeCompareApiResponse,
  NetRechargeCompareDetail,
  NetRechargeCompareDetailApi,
  NetRechargeCompareDetailParams,
  NetRechargeCompareItem,
  NetRechargeCompareListParams,
  NetRechargeCompareListResult,
  NetRechargeConsistencyQuery,
  NetRechargeFlowItem,
  NetRechargeOursLine,
  NetRechargeTicketStatusQuery,
} from './types/agent-reconciliation'

import type { PagingRequest } from '#/api/type'

import { formatDateTime } from '@vben/utils'

import { isNil } from 'lodash-es'

import { requestClient } from '#/api/request'
import { DateRanges } from '#/utils'

import {
  AgentTokenEnableStatus,
  NetRechargeConsistent,
  NetRechargeTicketStatus,
} from './types/agent-reconciliation'

export * from './types/agent-reconciliation'

export function getAgentTokenMemberValue(member: AgentTokenMember) {
  return member.agencyId ?? member.id
}

export function getAgentTokenMemberLabel(member: AgentTokenMember) {
  const enName = member.enName || member.name
  if (member.cnName && enName && member.cnName !== enName) {
    return `${member.cnName} (${enName})`
  }

  return member.cnName || enName || member.agencySystemId || ''
}

function normalizeTokenStatus(status: number) {
  if (status === 2) {
    return AgentTokenEnableStatus.Disabled
  }

  return status as AgentTokenEnableStatus
}

export function mapAgentTokenItem(item: AgentTokenApiItem): AgentTokenItem {
  const members = item.members ?? []
  const status = normalizeTokenStatus(item.status)

  return {
    ...item,
    agency: members
      .map((member) => getAgentTokenMemberValue(member))
      .filter((id): id is number => typeof id === 'number'),
    belongAgency: item.name,
    cnName: item.cnName || item.code,
    coveredCompleteDate: item.coverFinishDate,
    enableStatus: status,
    feishuStatus: item.feishuDisplay,
    lastSuccessAt:
      formatApiDateTime(item.lastFinishedAt) ?? item.lastFinishedAt,
    members,
    owner: item.tokenOwner,
    status,
    tokenStatus: item.tokenDisplay,
  }
}

function parseAmount(value?: number | string) {
  const amount = Number(value)
  return Number.isFinite(amount) ? amount : 0
}

function toPositiveInt(value?: number | string) {
  if (value === undefined || value === null || value === '') {
    return undefined
  }

  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return undefined
  }

  return parsed
}

function mapConsistencyQuery(
  value?: NetRechargeConsistent,
): NetRechargeConsistencyQuery | undefined {
  if (value === NetRechargeConsistent.Match) {
    return 'match'
  }

  if (value === NetRechargeConsistent.Mismatch) {
    return 'mismatch'
  }

  return undefined
}

const TICKET_STATUS_QUERY_MAP: Record<
  NetRechargeTicketStatus,
  NetRechargeTicketStatusQuery
> = {
  [NetRechargeTicketStatus.Closed]: 'closed',
  [NetRechargeTicketStatus.None]: 'none',
  [NetRechargeTicketStatus.Pending]: 'pending',
  [NetRechargeTicketStatus.Processing]: 'processing',
}

function mapTicketStatusQuery(
  value?: NetRechargeTicketStatus | string,
): NetRechargeTicketStatusQuery | undefined {
  if (!value) {
    return undefined
  }

  if (value in TICKET_STATUS_QUERY_MAP) {
    return TICKET_STATUS_QUERY_MAP[value as NetRechargeTicketStatus]
  }

  const normalized = String(value).trim().toLowerCase()
  return Object.values(TICKET_STATUS_QUERY_MAP).find(
    (item) => item === normalized,
  )
}

function mapTicketStatusFromApi(value?: string): NetRechargeTicketStatus {
  switch (value?.trim().toLowerCase()) {
    case 'closed': {
      return NetRechargeTicketStatus.Closed
    }
    case 'pending': {
      return NetRechargeTicketStatus.Pending
    }
    case 'processing': {
      return NetRechargeTicketStatus.Processing
    }
    default: {
      return NetRechargeTicketStatus.None
    }
  }
}

export function getNetRechargeCompareListCondition(
  condition?: NetRechargeCompareListParams,
) {
  return {
    adAccountId:
      condition?.adsAccountId?.trim() ||
      condition?.accountId?.trim() ||
      undefined,
    consistency: mapConsistencyQuery(condition?.consistent),
    currency: condition?.currency?.trim() || undefined,
    dateRange: DateRanges.dates(condition?.bizDate),
    groupId: toPositiveInt(condition?.groupId ?? condition?.agency),
    onlyMismatch: condition?.onlyMismatch || undefined,
    ticketStatus: mapTicketStatusQuery(condition?.ticketStatus),
  }
}

function formatApiDateTime(value?: string): string | undefined {
  const text = value?.trim()
  if (!text) {
    return undefined
  }

  const formatted = formatDateTime(text)
  return typeof formatted === 'string' ? formatted : text
}

export function getTicketListCondition(
  condition?: AgentReconciliationTicketListParams,
) {
  const status = mapTicketStatusQuery(condition?.status)

  return {
    adAccountId:
      condition?.adAccountId?.trim() ||
      condition?.accountId?.trim() ||
      undefined,
    dateRange: DateRanges.dates(condition?.bizDate),
    groupId: toPositiveInt(condition?.groupId ?? condition?.agency),
    status: status && status !== 'none' ? status : undefined,
    ticketNo: condition?.ticketNo?.trim() || undefined,
  }
}

export function mapTicketItem(
  item: AgentReconciliationTicketApiItem,
): AgentReconciliationTicketItem {
  return {
    accountId: item.adAccountId ?? '',
    agency: item.groupName ?? '',
    closedAt: formatApiDateTime(item.closedAt),
    closedBy: item.closedBy,
    closeRemark: item.closeRemark,
    bizDate: item.bizDate,
    createdAt: formatApiDateTime(item.createTime) ?? '',
    currency: item.currency,
    customerId: item.merchantId ?? '',
    customerName: item.customerName ?? '',
    groupId: item.groupId,
    id: String(item.id ?? ''),
    mediaPlatform: item.mediaPlatform,
    snapshotAgentNet: parseAmount(item.snapshotAgencyNet),
    snapshotCompanyNet: parseAmount(item.snapshotOursNet),
    snapshotDiff: parseAmount(item.snapshotDiff),
    startedAt: formatApiDateTime(item.startedAt),
    startedBy: item.startedBy,
    status: mapTicketStatusFromApi(item.status),
    ticketNo: item.ticketNo ?? '',
  }
}

export function getTicketDetailParams(
  id?: number | string,
  ticketNo?: string,
): AgentReconciliationTicketDetailParams {
  const parsedId = toPositiveInt(id)
  if (parsedId) {
    return { id: parsedId }
  }

  const parsedTicketNo = ticketNo?.trim() || String(id ?? '').trim()
  if (parsedTicketNo) {
    return { ticketNo: parsedTicketNo }
  }

  throw new Error('缺少工单ID或工单号')
}

function getCompareItemId(item: {
  accountId?: string
  bizDate?: string
  currency?: string
  groupId?: number
}) {
  return [item.bizDate, item.accountId, item.groupId, item.currency]
    .map((value) => value ?? '')
    .join('_')
}

export function mapNetRechargeCompareItem(
  item: NetRechargeCompareApiItem,
): NetRechargeCompareItem {
  const ticketStatus = mapTicketStatusFromApi(item.ticketStatus)
  const accountId = item.adAccountId ?? item.adsAccountId ?? ''
  const bizDate = item.bizDate

  return {
    accountId,
    agency: item.groupName ?? '',
    agentNetRecharge: parseAmount(item.agencyNet),
    companyNetRecharge: parseAmount(item.oursNet ?? item.ourNet),
    bizDate,
    consistent: item.isConsistent
      ? NetRechargeConsistent.Match
      : NetRechargeConsistent.Mismatch,
    currency: item.currency ?? '',
    customerId: item.merchantId ?? item.customerId ?? '',
    customerName: item.customerName ?? '',
    diffAmount: parseAmount(item.diff),
    groupId: item.groupId,
    hasTicket: item.hasTicket ?? ticketStatus !== NetRechargeTicketStatus.None,
    id: getCompareItemId({
      accountId,
      bizDate,
      currency: item.currency,
      groupId: item.groupId,
    }),
    mediaPlatform: item.mediaPlatform,
    ticketId: item.ticketId,
    ticketStatus,
    unmappedCount: item.unmappedCount,
  }
}

function mapAgencyLine(
  item: NetRechargeAgencyLine,
  index: number,
  prefix: string,
): NetRechargeFlowItem {
  return {
    amount: parseAmount(item.amount),
    bizNo: item.platformTxnId,
    bizTime: item.bizTime,
    bizTimeRaw: item.bizTimeRaw,
    id: `${prefix}-${index}-${item.platformTxnId || item.bizTimeRaw || index}`,
    type: item.bizType,
  }
}

function mapOursLine(
  item: NetRechargeOursLine,
  index: number,
): NetRechargeFlowItem {
  return {
    amount: parseAmount(item.amount),
    bizNo: item.bizNo,
    bizTime: item.createTime,
    id: `ours-${index}-${item.bizNo || item.createTime || index}`,
    type: item.type,
  }
}

export function mapTicketDetail(
  item: AgentReconciliationTicketDetailApi,
): AgentReconciliationTicketDetail {
  if (isNil(item.id) && !item.ticketNo) {
    throw new Error('记录不存在')
  }

  return {
    ...mapTicketItem(item),
    agentFlows: (item.agencyLinesSnapshot ?? []).map((line, index) =>
      mapAgencyLine(line, index, 'snapshot-agency'),
    ),
    companyOrders: (item.oursLinesSnapshot ?? []).map((line, index) =>
      mapOursLine(line, index),
    ),
    currentAgentNet: parseAmount(item.currentAgencyNet),
    currentCompanyNet: parseAmount(item.currentOursNet),
    currentDiff: parseAmount(item.currentDiff),
    currentIsConsistent: item.currentIsConsistent,
    currentMissing: item.currentMissing,
  }
}

export function mapNetRechargeCompareDetail(
  item: NetRechargeCompareDetailApi,
): NetRechargeCompareDetail {
  return {
    accountId: item.adAccountId ?? '',
    accountName: item.accountName,
    agency: item.groupName ?? '',
    agentFlows: (item.agencyLines ?? []).map((line, index) =>
      mapAgencyLine(line, index, 'agency'),
    ),
    agentNetRecharge: parseAmount(item.agencyNet),
    companyNetRecharge: parseAmount(item.oursNet),
    companyOrders: (item.oursLines ?? []).map((line, index) =>
      mapOursLine(line, index),
    ),
    bizDate: item.bizDate,
    consistent: item.isConsistent
      ? NetRechargeConsistent.Match
      : NetRechargeConsistent.Mismatch,
    currency: item.currency ?? '',
    customerId: item.merchantId,
    customerName: item.customerName ?? '',
    diffAmount: parseAmount(item.diff),
    groupId: item.groupId,
    mediaPlatform: item.mediaPlatform,
    unmappedFlows: (item.unmappedLines ?? []).map((line, index) =>
      mapAgencyLine(line, index, 'unmapped'),
    ),
  }
}

export function getNetRechargeCompareDetailParams(
  row: Pick<
    NetRechargeCompareItem,
    'accountId' | 'bizDate' | 'currency' | 'groupId' | 'mediaPlatform'
  >,
): NetRechargeCompareDetailParams {
  const groupId = toPositiveInt(row.groupId)
  if (!groupId) {
    throw new Error('缺少归类ID')
  }
  if (!row.accountId?.trim()) {
    throw new Error('缺少广告账户ID')
  }
  if (!row.bizDate) {
    throw new Error('缺少创建日')
  }

  return {
    adAccountId: row.accountId.trim(),
    bizDate: row.bizDate,
    groupId,
    ...(row.currency ? { currency: row.currency } : {}),
    ...(row.mediaPlatform ? { mediaPlatform: row.mediaPlatform } : {}),
  }
}

function getTicketId(id: number | string) {
  const ticketId = toPositiveInt(id)
  if (!ticketId) {
    throw new Error('缺少工单ID')
  }

  return ticketId
}

function getTokenListCondition(condition?: AgentTokenListParams) {
  return {
    agencySystemId: condition?.agencySystemId?.trim() || undefined,
    name:
      condition?.name?.trim() || condition?.belongAgency?.trim() || undefined,
    status: condition?.status ?? condition?.enableStatus,
    tokenDisplay: condition?.tokenDisplay,
  }
}

export const agentReconciliationApi = {
  async closeTicket(id: number | string, closeRemark?: string) {
    await requestClient.post('operation/agency-recon/ticket/close', {
      closeRemark: closeRemark?.trim() || undefined,
      id: getTicketId(id),
    })
  },
  async createTicket(
    params: AgentReconciliationCreateTicketParams,
  ): Promise<AgentReconciliationCreateTicketResult> {
    const res = await requestClient.post<{ existing?: boolean }>(
      'operation/agency-recon/ticket/create',
      {
        groupId: params.groupId,
        adAccountId: params.adAccountId,
        mediaPlatform: params.mediaPlatform,
        bizDate: params.bizDate,
        currency: params.currency,
      },
    )

    return {
      existing: Boolean(res?.existing),
    }
  },
  async queryNetRechargeCompare(
    params: PagingRequest<{ condition?: NetRechargeCompareListParams }>,
  ): Promise<NetRechargeCompareListResult> {
    const res = await requestClient.post<NetRechargeCompareApiResponse>(
      'operation/agency-recon/compare/list',
      {
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        condition: getNetRechargeCompareListCondition(params.condition),
      },
    )

    return {
      items: (res?.list ?? []).map((item) => mapNetRechargeCompareItem(item)),
      summary: {
        comparableCount: res?.summary?.totalRows ?? 0,
        creatableTicketCount: res?.summary?.openableRows ?? 0,
        mismatchCount: res?.summary?.mismatchRows ?? 0,
      },
      total: res?.total ?? 0,
    }
  },
  async queryNetRechargeCompareDetail(
    params: Partial<NetRechargeCompareDetailParams>,
  ): Promise<NetRechargeCompareDetail> {
    const res = await requestClient.post<NetRechargeCompareDetailApi>(
      'operation/agency-recon/compare/detail',
      params,
    )

    return mapNetRechargeCompareDetail(res ?? {})
  },
  async queryTicketDetail(
    id: number | string,
  ): Promise<AgentReconciliationTicketDetail> {
    const res = await requestClient.post<AgentReconciliationTicketDetailApi>(
      'operation/agency-recon/ticket/detail',
      getTicketDetailParams(id),
    )

    return mapTicketDetail(res ?? {})
  },
  async queryTickets(
    params: PagingRequest<{ condition?: AgentReconciliationTicketListParams }>,
  ): Promise<AgentReconciliationTicketListResult> {
    const res = await requestClient.post<AgentReconciliationTicketApiResponse>(
      'operation/agency-recon/ticket/list',
      {
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        condition: getTicketListCondition(params.condition),
      },
    )

    return {
      items: (res?.list ?? []).map((item) => mapTicketItem(item)),
      total: res?.total ?? 0,
    }
  },
  async startTicket(id: number | string) {
    await requestClient.post('operation/agency-recon/ticket/start', {
      id: getTicketId(id),
    })
  },
  async queryTokens(
    params: PagingRequest<{ condition?: AgentTokenListParams }>,
  ): Promise<AgentTokenListResult> {
    const res = await requestClient.post<{
      list?: AgentTokenApiItem[]
      total?: number
    }>('operation/agency-recon/group/query', {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      condition: getTokenListCondition(params.condition),
    })

    return {
      items: (res?.list ?? []).map((item) => mapAgentTokenItem(item)),
      total: res?.total ?? 0,
    }
  },
  async saveToken(params: AgentTokenSaveParams) {
    if (!params.agencySystemId?.trim()) {
      throw new Error('缺少代理后台编码')
    }

    await requestClient.post('operation/agency-recon/group/update', {
      agencySystemId: params.agencySystemId,
      cnName: params.cnName,
      id: params.id,
      members: params.members,
      name: params.name,
      remark: params.remark,
      status: params.status,
      timezone: params.timezone || 'Asia/Shanghai',
      tokenOwner: params.tokenOwner,
    })
  },
}
